import React from 'react';
import { OrdemDeServicoFormData, AjudanteOSRow } from '../../types/ordemServico';
import { Users, Plus, Trash2, DollarSign } from 'lucide-react';

interface OsFormTabAjudantesProps {
  formData: OrdemDeServicoFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrdemDeServicoFormData>>;
  pessoasOptions: { id: number; nome: string }[];
}

export const OsFormTabAjudantes: React.FC<OsFormTabAjudantesProps> = ({
  formData,
  setFormData,
  pessoasOptions,
}) => {
  const handleAddAjudante = () => {
    setFormData((prev) => ({
      ...prev,
      ajudantes_list: [
        ...prev.ajudantes_list,
        { ajudantes_id: '', repasse_ajudante: 0 },
      ],
    }));
  };

  const handleRemoveAjudante = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      ajudantes_list: prev.ajudantes_list.filter((_, i) => i !== index),
    }));
  };

  const handleRowChange = (index: number, field: keyof AjudanteOSRow, value: any) => {
    setFormData((prev) => {
      const updatedList = [...prev.ajudantes_list];
      updatedList[index] = {
        ...updatedList[index],
        [field]: field === 'repasse_ajudante' ? (value === '' ? 0 : parseFloat(value)) : value,
      };

      return {
        ...prev,
        ajudantes_list: updatedList,
      };
    });
  };

  const totalAjudantesRepasse = formData.ajudantes_list.reduce(
    (acc, item) => acc + (Number(item.repasse_ajudante) || 0),
    0
  );

  return (
    <div className="space-y-4 p-4 bg-slate-50 border border-slate-200/80 rounded-xl">
      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-red-600" />
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
            Lista de Ajudantes Escalados & Repasses
          </h4>
        </div>

        <button
          type="button"
          onClick={handleAddAjudante}
          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1 shadow-xs"
        >
          <Plus className="w-3.5 h-3.5" />
          Adicionar Ajudante
        </button>
      </div>

      {formData.ajudantes_list.length === 0 ? (
        <div className="py-8 text-center text-slate-400 bg-white border border-dashed border-slate-300 rounded-lg text-xs">
          Nenhum ajudante adicionado à lista. Clique em "Adicionar Ajudante" para incluir.
        </div>
      ) : (
        <div className="space-y-3">
          {formData.ajudantes_list.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center bg-white p-3 border border-slate-200 rounded-lg shadow-2xs"
            >
              <div className="sm:col-span-7">
                <label className="block text-2xs font-bold text-slate-500 uppercase mb-1">
                  Ajudante #{index + 1}
                </label>
                <select
                  value={row.ajudantes_id}
                  onChange={(e) => handleRowChange(index, 'ajudantes_id', e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-xs text-slate-800 outline-none focus:ring-2 focus:ring-red-500 bg-white"
                >
                  <option value="">Selecione a pessoa/ajudante...</option>
                  {pessoasOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-4">
                <label className="block text-2xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                  <DollarSign className="w-3 h-3 text-emerald-600" />
                  Valor Repasse (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={row.repasse_ajudante}
                  onChange={(e) => handleRowChange(index, 'repasse_ajudante', e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-xs font-bold text-slate-800 outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="sm:col-span-1 flex justify-end sm:justify-center pt-2 sm:pt-4">
                <button
                  type="button"
                  onClick={() => handleRemoveAjudante(index)}
                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  title="Remover Ajudante"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <div className="p-3 bg-red-50/70 border border-red-100 rounded-lg flex items-center justify-between text-xs">
            <span className="font-bold text-red-800 uppercase">
              Total Repasse Ajudantes ({formData.ajudantes_list.length} pessoa(s)):
            </span>
            <span className="font-bold text-red-700 text-sm">
              R$ {totalAjudantesRepasse.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
